import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Zap,
  TrendingUp,
  DollarSign,
  Settings,
  Download,
  Filter,
  BarChart3,
  Calendar
} from 'lucide-react';
import TariffSettingsDialog from './TariffSettingsDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EnergyDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tariff, setTariff] = useState(1500);
  const [showTariffDialog, setShowTariffDialog] = useState(false);
  
  // Filters
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filterStartDate) params.append('start_date', filterStartDate);
      if (filterEndDate) params.append('end_date', filterEndDate);
      if (selectedUnit) params.append('unit_id', selectedUnit);
      
      const [statsResponse, tariffResponse] = await Promise.all([
        axios.get(`${API}/energy/stats?${params}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/energy/tariff`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setStats(statsResponse.data.stats);
      setTariff(tariffResponse.data.tariff);
    } catch (error) {
      console.error('Error fetching energy data:', error);
      if (error.response?.status !== 403) {
        toast.error('Gagal memuat data energy');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTariffUpdate = () => {
    setShowTariffDialog(false);
    fetchData();
    toast.success('Tarif listrik berhasil diupdate');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const exportToCSV = () => {
    if (!stats || !stats.by_unit || stats.by_unit.length === 0) {
      toast.error('Tidak ada data untuk di-export');
      return;
    }

    const headers = ['Unit', 'Total kWh', 'Biaya', 'Jumlah Rental'];
    const rows = stats.by_unit.map(unit => [
      unit.unit_name,
      unit.total_kwh,
      unit.total_cost,
      unit.rental_count
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(',')),
      '',
      `Total,${stats.total_kwh},${stats.total_cost},${stats.total_rentals}`
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `energy-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast.success('Data berhasil di-export');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="h-7 w-7 text-yellow-500" />
            Energy Cost Tracking
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Monitor konsumsi listrik & biaya operasional
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowTariffDialog(true)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all font-semibold"
          >
            <Settings className="h-5 w-5" />
            Tarif Listrik
          </button>
        </div>
      </div>

      {/* Current Tariff Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Zap className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Tarif Listrik Saat Ini</p>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(tariff)}/kWh</p>
            </div>
          </div>
          <button
            onClick={() => setShowTariffDialog(true)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Ubah →
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <Zap className="h-8 w-8 opacity-80" />
              <span className="text-sm opacity-80">Total Konsumsi</span>
            </div>
            <div className="text-3xl font-bold">{stats.total_kwh} kWh</div>
            <div className="text-sm opacity-80 mt-1">{stats.total_rentals} rental</div>
          </div>

          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="h-8 w-8 opacity-80" />
              <span className="text-sm opacity-80">Total Biaya</span>
            </div>
            <div className="text-2xl font-bold">{formatCurrency(stats.total_cost)}</div>
            <div className="text-sm opacity-80 mt-1">Listrik</div>
          </div>

          <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center justify-between mb-2">
              <TrendingUp className="h-8 w-8 opacity-80" />
              <span className="text-sm opacity-80">Rata-rata</span>
            </div>
            <div className="text-2xl font-bold">{stats.average_kwh_per_rental} kWh</div>
            <div className="text-sm opacity-80 mt-1">Per Rental</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-5 w-5 text-gray-500" />
          <span className="font-semibold text-gray-700">Filter Data</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Dari Tanggal
            </label>
            <input
              type="date"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Sampai Tanggal
            </label>
            <input
              type="date"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchData}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold"
            >
              Terapkan Filter
            </button>
          </div>
          <div className="flex items-end">
            <button
              onClick={exportToCSV}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* By Unit Table */}
      {stats && stats.by_unit && stats.by_unit.length > 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-blue-600" />
              Konsumsi per Unit
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Unit
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Total kWh
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Biaya Listrik
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Jumlah Rental
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Rata-rata/Rental
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {stats.by_unit.map((unit, index) => {
                  const avgPerRental = unit.rental_count > 0 ? unit.total_kwh / unit.rental_count : 0;
                  
                  return (
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-yellow-100 rounded-lg">
                            <Zap className="h-4 w-4 text-yellow-600" />
                          </div>
                          <span className="text-sm font-medium text-gray-900">{unit.unit_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-gray-900">{unit.total_kwh} kWh</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm font-bold text-green-600">{formatCurrency(unit.total_cost)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {unit.rental_count} rental
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="text-sm text-gray-600">{avgPerRental.toFixed(2)} kWh</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td className="px-6 py-4 whitespace-nowrap font-bold text-gray-900">
                    TOTAL
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-gray-900">
                    {stats.total_kwh} kWh
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-green-600">
                    {formatCurrency(stats.total_cost)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-gray-900">
                    {stats.total_rentals} rental
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-gray-900">
                    {stats.average_kwh_per_rental} kWh
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Zap className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-gray-500 mb-2">Belum ada data energy</p>
          <p className="text-sm text-gray-400">Data akan muncul setelah rental dengan energy tracking selesai</p>
        </div>
      )}

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-gray-800 mb-1">Informasi Energy Tracking</p>
            <ul className="text-xs text-gray-600 space-y-1">
              <li>• Data energy otomatis terekam saat rental dimulai dan selesai</li>
              <li>• Tarif listrik dapat diubah sesuai tarif PLN Anda</li>
              <li>• Export data untuk laporan bulanan biaya operasional</li>
              <li>• Hanya rental dengan Bardi smart plug yang support energy tracking</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Tariff Settings Dialog */}
      {showTariffDialog && (
        <TariffSettingsDialog
          open={showTariffDialog}
          currentTariff={tariff}
          onClose={() => setShowTariffDialog(false)}
          onSuccess={handleTariffUpdate}
        />
      )}
    </div>
  );
}
